import Axios from 'axios'
import logger from '@/lib/logger'
import { emitIncompleteSetupRecord } from './lib/viewer_telemetry'

/**
 * Builds the `telemetry` option passed into WebRTCSession.connect(). Lives outside lib/webrtc/lib
 * so it can reach the Vuex store and the API base URL without coupling the framework-agnostic
 * collector to them.
 *
 * Collection only runs when the connected device has "Video Diagnostics" enabled
 * (joinData.webrtc_diagnostics_enabled). The emit transport always uses fetch with keepalive so
 * the payload survives a tab close while still carrying the Auth0 Bearer token (which sendBeacon
 * cannot attach). NOTE: when the API is cross-origin, the Authorization/Content-Type headers
 * force a CORS preflight; a preflight during page unload is not guaranteed to complete, so the
 * pagehide emit can be lost cross-origin. The 30s and session-end emits (page still alive) are
 * unaffected. Switch to navigator.sendBeacon (token in body) if reliable teardown delivery is
 * required.
 *
 * See design/webrtc-viewer-telemetry.md.
 *
 * @param {object} store - the Vuex store
 * @param {object} joinData - the join response payload
 * @param {{ camera: string, webrtcCallId: string }} opts - camera is 'internal' | 'ptz'; webrtcCallId is the attempt's id
 */
export function buildViewerTelemetryOptions(store, joinData, { camera, webrtcCallId }) {
  const url = `${import.meta.env.VITE_API_BASE_URL}/v1/videos/webrtc_telemetry`
  return {
    diagnosticsEnabled: joinData.webrtc_diagnostics_enabled === true,
    sessionId: store.state.uniqueSessionId,
    telemetryId: webrtcCallId,
    webrtcCallId,
    camera,
    appVersion: import.meta.env.VITE_BUILD,
    emit: (payload) => {
      fetch(url, {
        method: 'POST',
        keepalive: true,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${store.state.token}`,
          'Session-Id': store.state.uniqueSessionId
        },
        body: JSON.stringify(payload)
      })
        .then(res => logger.info('telemetry payload sent', { status: res.status, telemetry_id: payload.telemetry_id }))
        .catch(err => logger.warn('telemetry payload send failed', err)) // never surfaced to the call
    }
  }
}

/**
 * Sends one setup record for an attempt that ended before its peer connection existed (see
 * design/webrtc-viewer-telemetry.md, "Setup records"). A failed join has no reply, so it is sent
 * and Rails decides whether to store it; every other ending follows the reply's Video
 * Diagnostics flag, as the call's record does. Rails drops any record for a device with Video
 * Diagnostics off. A join the browser cancelled itself sends nothing.
 *
 * This is the setup record's only error boundary: it never throws, whatever it is passed, so its
 * callers on startVideo's cleanup paths need no guard of their own.
 *
 * @param {object} store - the Vuex store
 * @param {object} ctx - { joinData, deviceId, channel, isPtz, startedAt, webrtcCallId }; joinData is null for a failed join
 * @param {object} end - { stage: 'join' | 'user_media', err, stopped }
 */
export function sendIncompleteSetupRecord(store, ctx, end) {
  try {
    const { joinData = null, deviceId, channel, isPtz = false, startedAt = null, webrtcCallId } = ctx || {}
    // Cancelled by the inactivity guard (lib/axios.js): the join never reached Rails and the session
    // token is dead, so the record would only earn a 401.
    if (Axios.isCancel(end?.err)) return
    const telemetry = buildViewerTelemetryOptions(store, joinData || {}, { camera: isPtz ? 'ptz' : 'internal', webrtcCallId })
    const joinFailed = end?.stage === 'join' && end?.err != null
    if (!joinFailed && !telemetry.diagnosticsEnabled) return
    emitIncompleteSetupRecord(telemetry, { deviceId, peerId: store.state.currentUser?.id, channel, startedAt }, end)
  } catch (err) {
    logger.warn('setup record not sent', err)
  }
}
